'use client';
/**
 * Двухуровневая навигация.
 *
 * Первый уровень — 12 рабочих областей, всегда видны. Второй раскрывается
 * только у активной области (и у той, что открыл пользователь): постоянно
 * развёрнутый аккордеон на 62 пункта — это тот же список, от которого мы
 * уходим, только с отступами.
 *
 * Driver и applicant получают плоский список из того же конфига: у них
 * узкий workflow, группировать не во что.
 */
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useT } from '@/lib/useT';
import { useModules } from '@/lib/useModules';
import { cx } from '@/components/ui';
import {
  ADMIN_AREAS, DRIVER_ITEMS, APPLICANT_ITEMS, areaForPath,
  type NavArea, type NavLeaf, type Role,
} from '@/lib/navigation';

const COLLAPSE_KEY = 'logixa_sidebar_collapsed';

export default function SidebarNav({ role }: { role: Role }) {
  const pathname = usePathname() || '';
  const t = useT();
  const modules = useModules();   // Record<ModuleKey, boolean>
  const [collapsed, setCollapsed] = useState(false);
  const [openArea, setOpenArea] = useState<string | null>(null);

  useEffect(() => {
    try { setCollapsed(localStorage.getItem(COLLAPSE_KEY) === '1'); } catch {}
  }, []);
  useEffect(() => {
    // Активная область раскрывается сама — иначе после перезагрузки
    // непонятно, где ты находишься.
    const area = areaForPath(pathname);
    if (area) setOpenArea(area.id);
  }, [pathname]);
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', collapsed ? '56px' : '232px');
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  const label = (l: { labelKey: string; fallback: string }) => {
    const v = t(l.labelKey);
    return !v || v === l.labelKey ? l.fallback : v;
  };
  const visible = (leaf: NavLeaf) => !leaf.hidden && (!leaf.module || modules[leaf.module] !== false);
  const isActive = (href: string) => pathname === href || pathname.startsWith(href + '/');

  return (
    <aside
      className={cx(
        'fixed inset-y-0 left-0 z-40 flex flex-col',
        'bg-[var(--surface)] border-r border-[var(--border)]',
        'transition-[width] duration-150 ease-out',
      )}
      style={{ width: collapsed ? 56 : 232 }}
    >
      {/* Бренд */}
      <div className={cx('flex items-center h-[52px] shrink-0 border-b border-[var(--border)]', collapsed ? 'justify-center px-2' : 'px-3.5')}>
        <Link href="/dashboard" className="flex items-center gap-2 min-w-0 focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)] rounded-[var(--radius-sm)]">
          <span className="text-[16px] font-bold tracking-[-0.03em] text-[var(--text-primary)]">
            {collapsed ? <>L<span className="text-[var(--brand)]">X</span></> : <>LOGI<span className="text-[var(--brand)]">X</span>A</>}
          </span>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2" aria-label="Main">
        {role === 'admin'
          ? ADMIN_AREAS.map((area) => (
              <AreaBlock
                key={area.id} area={area} collapsed={collapsed}
                open={openArea === area.id} active={!!areaForPath(pathname) && areaForPath(pathname)!.id === area.id}
                onToggle={() => setOpenArea((p) => (p === area.id ? null : area.id))}
                label={label} visible={visible} isActive={isActive}
              />
            ))
          : (role === 'driver' ? DRIVER_ITEMS : APPLICANT_ITEMS)
              .filter(visible)
              .map((leaf) => (
                <FlatItem key={leaf.href} leaf={leaf} collapsed={collapsed} active={isActive(leaf.href)} label={label} />
              ))}
      </nav>

      <button
        onClick={() => setCollapsed((c) => !c)}
        className={cx(
          'flex items-center gap-2 h-10 shrink-0 border-t border-[var(--border)] text-[12px]',
          'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)] transition-colors',
          'focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]',
          collapsed ? 'justify-center px-2' : 'px-3.5',
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <PanelLeftOpen size={15} /> : <><PanelLeftClose size={15} /> Collapse</>}
      </button>
    </aside>
  );
}

/* ── Область первого уровня ─────────────────────────────────────── */

function AreaBlock({ area, collapsed, open, active, onToggle, label, visible, isActive }: {
  area: NavArea; collapsed: boolean; open: boolean; active: boolean; onToggle: () => void;
  label: (l: { labelKey: string; fallback: string }) => string;
  visible: (l: NavLeaf) => boolean;
  isActive: (href: string) => boolean;
}) {
  const Icon = area.icon;
  const items = area.items.filter(visible);
  const leaf = !items.length && area.href;
  const selfActive = area.href ? isActive(area.href) : false;

  const rowClass = cx(
    'group flex items-center gap-2.5 w-full h-9 text-[13px] font-medium transition-colors',
    'focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]',
    collapsed ? 'justify-center px-2' : 'px-3.5',
    // Заливка активного пункта — едва заметный оттенок бренда: полноценный
    // персиковый фон читался как предупреждение. Цвет несёт индикатор слева.
    active || selfActive
      ? 'text-[var(--text-primary)] font-semibold bg-[color-mix(in_srgb,var(--brand)_5%,transparent)]'
      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]',
  );

  // Активная область помечена оранжевой полосой слева — цветом действия,
  // а не заливкой всего пункта.
  const marker = (active || selfActive) && (
    <i className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-[2px] bg-[var(--brand)]" aria-hidden />
  );

  if (leaf) {
    return (
      <div className="relative">
        {marker}
        <Link href={area.href!} className={rowClass} title={collapsed ? label(area) : undefined}>
          <Icon size={16} strokeWidth={2} className={cx('shrink-0', (active || selfActive) && 'text-[var(--brand)]')} />
          {!collapsed && <span className="truncate">{label(area)}</span>}
        </Link>
      </div>
    );
  }

  return (
    <div className="relative">
      {marker}
      <button onClick={onToggle} className={rowClass} aria-expanded={open} title={collapsed ? label(area) : undefined}>
        <Icon size={16} strokeWidth={2} className={cx('shrink-0', active && 'text-[var(--brand)]')} />
        {!collapsed && (
          <>
            <span className="truncate">{label(area)}</span>
            <ChevronRight size={13} className={cx('ml-auto shrink-0 transition-transform text-[var(--text-muted)]', open && 'rotate-90')} />
          </>
        )}
      </button>
      {open && !collapsed && (
        <ul className="pb-1">
          {items.map((leafItem) => {
            const on = isActive(leafItem.href);
            return (
              <li key={leafItem.href} className="relative">
                <Link
                  href={leafItem.href}
                  className={cx(
                    'flex items-center h-8 pl-[42px] pr-3 text-[12.5px] transition-colors',
                    'focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]',
                    on
                      ? 'text-[var(--brand)] font-medium'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]',
                  )}
                >
                  {on && <i className="absolute left-[26px] w-1.5 h-1.5 rounded-full bg-[var(--brand)]" aria-hidden />}
                  <span className="truncate">{label(leafItem)}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ── Плоский пункт: driver / applicant ──────────────────────────── */

function FlatItem({ leaf, collapsed, active, label }: {
  leaf: NavLeaf; collapsed: boolean; active: boolean;
  label: (l: { labelKey: string; fallback: string }) => string;
}) {
  const Icon = leaf.icon;
  return (
    <div className="relative">
      {active && <i className="absolute left-0 top-1 bottom-1 w-[2px] rounded-r bg-[var(--brand)]" aria-hidden />}
      <Link
        href={leaf.href}
        title={collapsed ? label(leaf) : undefined}
        className={cx(
          'flex items-center gap-2.5 h-9 text-[13px] font-medium transition-colors',
          'focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]',
          collapsed ? 'justify-center px-2' : 'px-3.5',
          active
            ? 'text-[var(--text-primary)] font-semibold bg-[color-mix(in_srgb,var(--brand)_5%,transparent)]'
            : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]',
        )}
      >
        {Icon && <Icon size={16} strokeWidth={2} className={cx('shrink-0', active && 'text-[var(--brand)]')} />}
        {!collapsed && <span className="truncate">{label(leaf)}</span>}
      </Link>
    </div>
  );
}
